/**
 * The one place that talks to the server.
 *
 * Carried over from the old client almost unchanged, because it was already
 * right: the token lives in localStorage so a reload keeps the session, a 401
 * is a signal rather than an error to swallow, and a failure carries the
 * server's own words rather than "Request failed".
 */

const TOKEN_KEY = "wire.token";
const EMAIL_KEY = "wire.email";

/* The account an admin is looking at the app as (see `server/app/viewas.py`).
   Per tab, in sessionStorage: it survives the reload that starts it and a
   refresh, and is gone with the tab, so a view forgotten in one window does
   not greet the admin in the next one as somebody else's dashboard. */
const VIEW_AS_KEY = "wire.viewAs";
const VIEW_AS_HEADER = "X-Wire-View-As";

function viewing() {
  try {
    return sessionStorage.getItem(VIEW_AS_KEY) || "";
  } catch {
    return "";
  }
}

export const session = {
  get token() {
    return localStorage.getItem(TOKEN_KEY);
  },
  get email() {
    return localStorage.getItem(EMAIL_KEY) || "";
  },
  get viewing() {
    return viewing();
  },
  sign_in(token, email) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(EMAIL_KEY, email || "");
  },
  sign_out() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    session.stop_viewing();
  },
  view_as(email) {
    sessionStorage.setItem(VIEW_AS_KEY, email);
  },
  stop_viewing() {
    try {
      sessionStorage.removeItem(VIEW_AS_KEY);
    } catch {
      // Nothing was stored to remove.
    }
  },
};

let onUnauthorized = () => {};

/** Told once, by the shell, what to do when the server stops believing us. */
export function whenUnauthorized(handler) {
  onUnauthorized = handler;
}

/* Where FastAPI says a rejected value came from. It leads every `loc`, and it
   is the one part of the path that means nothing to somebody filling in a
   form: they did not put a field "in the body". */
const SOURCES = new Set(["body", "query", "path", "header", "cookie"]);

/**
 * The server's `detail`, as a sentence.
 *
 * Usually it already is one. A request that fails validation is the exception:
 * FastAPI answers 422 with a list, one `{loc, msg}` per rejected value, and
 * only a string was being read — so the one failure where the server says
 * exactly which field is wrong, and why, came out as "Request failed (422)"
 * (#967).
 */
function readable(detail) {
  if (typeof detail === "string") return detail;
  if (!Array.isArray(detail)) return "";
  return detail
    .map((item) => {
      if (typeof item === "string") return item;
      const where = (Array.isArray(item?.loc) ? item.loc : [])
        .filter((part, index) => !(index === 0 && SOURCES.has(part)))
        .join(".");
      const what = typeof item?.msg === "string" ? item.msg : "";
      return where && what ? `${where}: ${what}` : what || where;
    })
    .filter(Boolean)
    .join("; ");
}

async function failureDetail(response) {
  try {
    const body = await response.json();
    const detail = readable(body.detail);
    if (detail) return detail;
  } catch {
    /* no JSON body — fall through to the status line */
  }
  return `Request failed (${response.status})`;
}

/**
 * `fetch`, with a dead connection said in words.
 *
 * `fetch` rejects when there was no answer at all — the server down, the
 * network gone, a restart in progress — and what it rejects with is the
 * browser's own "Failed to fetch" or "NetworkError when attempting to fetch
 * resource", which went straight into a toast (#967). The failure is marked
 * `unreachable` and carries no `status`, because there was no response to
 * have one: that is how a caller tells silence from a refusal.
 */
async function send(path, init) {
  try {
    return await fetch(`/api${path}`, init);
  } catch (error) {
    // A request somebody cancelled is not a server that cannot be reached.
    if (error?.name === "AbortError") throw error;
    const failure = new Error("Cannot reach the server.", { cause: error });
    failure.unreachable = true;
    throw failure;
  }
}

/* A server that is busy says so: 503, with `Retry-After` naming when to come
   back. That is what lock contention and an empty connection pool answer with
   now, where both used to be a bare 500 that read as a bug (#966). A read is
   asked again, twice, because nothing is lost by asking and the reader would
   only have pressed the button again. The wait is whatever the server asked
   for, capped: a page will sit for five seconds on a busy server, and no
   header from anywhere is allowed to make it sit for an hour. */
const BUSY_RETRIES = 2;
const MAX_BUSY_WAIT_MS = 5000;
// What to wait when a 503 came with no usable `Retry-After`, which is what a
// proxy answering for a server that is restarting tends to send.
const DEFAULT_BUSY_WAIT_MS = 1000;

const BUSY_READ = "The server is busy. Try again in a moment.";
/* For a write, said so that nobody wonders whether to press it again. A 503 is
   a refusal made before the work began, so there is nothing half done. */
const BUSY_WRITE = "The server is busy, so that was not done. Try it again in a moment.";

/** `Retry-After` in milliseconds. It is seconds or an HTTP date, by the RFC. */
function busyWaitMs(response) {
  const header = (response.headers.get("Retry-After") || "").trim();
  let wait = DEFAULT_BUSY_WAIT_MS;
  if (/^\d+$/.test(header)) {
    wait = Number(header) * 1000;
  } else if (header) {
    const at = Date.parse(header);
    if (!Number.isNaN(at)) wait = at - Date.now();
  }
  return Math.min(Math.max(wait, 0), MAX_BUSY_WAIT_MS);
}

const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export async function request(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  const token = session.token;
  if (token) headers.Authorization = `Bearer ${token}`;
  const as = session.viewing;
  if (as) headers[VIEW_AS_HEADER] = as;

  /* Only a GET is asked again by itself. Anything else may have been a
     write, and whether a write is safe to repeat is something the person who
     made it knows and this function does not. */
  const read = (options.method || "GET").toUpperCase() === "GET";

  let response = await send(path, { ...options, headers });
  for (let asked = 0; read && response.status === 503 && asked < BUSY_RETRIES; asked += 1) {
    await pause(busyWaitMs(response));
    response = await send(path, { ...options, headers });
  }

  if (response.status === 401) {
    onUnauthorized();
    // Carries its status like every other failure. The boot check has to tell
    // "the server refused this token" from "the server did not answer", and
    // without this the first looked exactly like the second: an error with no
    // status on it. Guessing wrong there deleted a good session (#957).
    const refused = new Error("Session expired — sign in again.");
    refused.status = 401;
    throw refused;
  }
  if (!response.ok) {
    // The status travels with the message. Without it every caller has to
    // treat "gone" and "the server fell over for a second" the same way, and
    // the ones that guess pick the wrong one — a trip view that redirects on
    // any failure tells the reader their trip was deleted when the connection
    // dropped.
    //
    // A 503 gets a sentence of its own instead of the server's. What it says
    // there is why it was busy, which is for the system log; what the reader
    // needs is that nothing is broken and the same action will work shortly.
    const busy = response.status === 503;
    const failure = new Error(
      busy ? (read ? BUSY_READ : BUSY_WRITE) : await failureDetail(response),
    );
    failure.status = response.status;
    throw failure;
  }
  return response;
}

export async function api(path, options = {}) {
  let next = options;
  // A FormData body must keep the browser's own multipart content type, which
  // includes the boundary; setting JSON here would corrupt every upload.
  if (options.body && !(options.body instanceof FormData)) {
    next = {
      ...options,
      headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    };
  }
  const response = await request(path, next);
  return response.status === 204 ? null : response.json();
}

export const get = (path) => api(path);
/** FormData goes through untouched — `api` leaves its multipart boundary alone,
 *  and stringifying it here would post the literal "[object FormData]". */
export const post = (path, body) =>
  api(path, {
    method: "POST",
    body:
      body === undefined || body instanceof FormData ? body : JSON.stringify(body),
  });
export const put = (path, body) => api(path, { method: "PUT", body: JSON.stringify(body) });
export const patch = (path, body) => api(path, { method: "PATCH", body: JSON.stringify(body) });
export const del = (path) => api(path, { method: "DELETE" });
